export default {
  designSummaryRedo: 'Redo the summary with AI',
  designSummaryRedoConfirm: 'The summary will be rewritten from the design as it stands today. The current text is replaced; activities and evaluations are untouched.',
  // ========== Header ==========
  title: 'Instructional Design',
  method: 'Model',
  methodPlaceholder: 'Select a model',
  methodNone: 'No model defined',
  methodTooltip:
    'Instructional model used for this subject. It defines the available moments.',
  methodsEmpty: 'There are no instructional models created.',
  methodUpdated: 'Model updated.',

  // ========== Statuses ==========
  // E28 · The five fixed statuses became a per-institution catalogue
  // (InstructionalStatus). What stays here is the absence of a status, which is not
  // a catalogue entry.
  statusNotStarted: 'Not started',

  // ========== Summary table ==========
  summaryTitle: 'Instructional Design Summary',
  summaryWeek: 'Week',
  summaryStudyUnits: 'Study Units',
  summaryIndicators: 'Indicators',
  summaryActivities: 'Activities',
  summaryStatus: 'Status',
  summaryWeighing: 'Weighing',
  // E24: the table lists both design activities and program evaluations.
  summaryActivityOrEvaluation: '{activity} / {evaluation}',
  summaryEmpty: 'There are no {entity} in this instructional design yet.',
  summaryNoStudyUnits: 'No {entity} assigned',

  // ========== Containers (alternative name: manual.instructional_design.container) ==========
  modulesTitle: 'Content {entity}',
  moduleTitleField: 'Title',
  moduleDescriptionField: 'Description',
  moduleTitlePlaceholder: 'E.g.: {entity} 1 · Foundations',
  moduleUntitled: 'Untitled {entity}',
  moduleNoActivities: 'This {entity} has no {activities} yet. Create one to get started.',
  moduleDeleteKeepsActivities: 'The {count} {activities} it contains are NOT deleted: they are left without a {container}, in the unplaced block, and can be dragged back. Their instruments and grades are untouched.',
  moduleStudyUnitsAuto: 'The {study_unit} of this {entity} are derived from the {indicator} of the activities and evaluations it holds.',
  studyUnitFallback: 'Unit {order}',
  moduleStudyUnitsEmpty: 'They will appear once you place {entity} here',

  // ========== Activities (alternative name: manual.instructional_design.activity) ==========
  activitiesTitle: 'Activities by Moment',
  activitiesWithoutModule: '{activity} without a {container}',
  activityNoModule: 'No {entity}',
  activityEmpty:
    'There are no {entity} yet. Create the first one: choose its Study Units and Achievement Indicators, and the rest falls into place.',
  activityUntitled: 'Untitled {entity}',
  activityTitleField: 'Title',
  activityHoursField: 'Hours',
  activityAutonomousHoursField: 'Autonomous hours',
  activityAutonomousHoursTooltip:
    'Student autonomous work (preparation and/or execution). It happens within the same week: it does not move it forward.',
  activityWeek: 'Week {week}',
  activityWeekRange: 'Weeks {from}-{to}',
  activityWeekTooltip: 'Week calculated by accumulating the classroom hours of the previous {activities} of the design.',
  activityWeekOverflow: 'This {activity} falls outside the weeks the {matter} has. Review the hours of the design.',
  activityWeekMissingNoHours: 'No week: hours are missing',
  activityWeekMissingChainBroken: 'No week: an earlier {activity} has no hours',
  activityWeekMissingNoClassroomHours:
    'No week: the subject has no pedagogical hours loaded',
  activityParticipationField: 'Participation',
  activityStudentInstructions: 'Instructions for the student',
  activityProfessorInstructions: 'Instructions for the teacher',
  activityOrientation: 'Orientation',
  activityOrientationEmpty: 'No orientation yet. Click to write it.',
  activityOrientationEdit: 'Edit the orientation',
  activityAttachedFiles: 'Attached Files',
  activityAttachedFilesEmpty: 'No attached files.',
  activityIndexModule: 'M{order}',
  designSummary: 'Design summary',
  designSummaryEmpty: 'No summary yet. Click to describe the methodology, how it is assessed and the student role.',
  designSummaryEdit: 'Edit the design summary',
  descriptor: 'Descriptor',
  descriptorsTitle: 'Descriptors',
  descriptorsHint: 'Free text blocks for whatever the design has no field of its own for. The title is added to the institution catalogue and stays available for other designs; the content belongs to this design.',
  descriptorsTopTitle: 'Descriptors at the top',
  descriptorsTopHint: 'The same as the Descriptors at the end, but before the sequence: for whatever is read first, such as a framing note or a warning. The title is added to the institution catalogue and stays available for other designs; the content belongs to this design. Each title belongs to one zone only.',
  activityDescriptorsTitle: 'Descriptors',
  activityDescriptorsHint: 'Extra fields for this activity: a video, a link, another paragraph, whatever you need that has no field of its own. You give it the title you want and add as many as you want. The title is stored in the institution catalogue; the content belongs to this activity.',
  activityIndicators: 'Achievement Indicators',
  activityContents: 'Contents',
  referencedEvaluationTooltip: '{evaluation} created in the {program}. The Instructional Design places it in the sequence.',
  referencedEvaluationOrigin: 'This {evaluation} is defined in the {program}. Here you decide where it goes in the learning sequence.',
  activityMomentsField: 'Moments it activates',
  activityMomentsHint: 'An {activity} can activate several moments at once: they are not separate steps but interconnected phases. Its place in the sequence comes from its order within the {container}, not from a moment.',
  activityMomentsGuide: "See this model's moments and their description",
  activityListEmpty: 'No {entity}.',
  activityStrategiesEmpty: 'The institution has no {entity} loaded yet.',
  activityStrategiesMatter: 'Declared as a minimum for the {entity}.',
  activityStrategiesNone: 'No {entity} selected.',
  activityStrategiesCreateWarning:
    'It is added to the institution catalogue: it will be available in every subject, including the program and the planning.',
  activityEvidencesEmpty: 'The institution has no {entity} loaded yet.',
  activityEvidencesHint: 'What the student produces with this {entity}. It is chosen from the institution catalogue; to add a new one, it is managed in its own view.',
  activityContentsEmpty: 'Select the {indicator} first: the available contents are those of their {study_unit}.',
  activityNoDescription: 'No description',
  activityNoInstructions: 'No instructions',
  participationIndividual: 'Individual',
  participationPairs: 'Pairs',
  participationGroup: 'Group',
  activityInstrumentMissing: 'The {evaluation} linked to this {activity} no longer exists.',
  activityGoTo: 'Go to {entity}',
  activityGoToTooltip: 'Open the {entity} to edit its questions',
  activityDeleteFromProgramWarning: 'This {evaluation} is an {entity} of the Instructional Design: it will be removed from there too.',

  // ========== Activity bibliography ==========

  // ========== Coverage ==========
  coverageTitle: 'Pending coverage',
  coverageUsageTitle: 'Use of {entity} across the design',
  coverageUsageExpand:
    'See what it is taught and assessed with, and what the student produces',
  coherenceTaughtWith: 'Worked on in',
  coherenceEvaluatedWith: 'Assessed in',
  coherenceNoStrategies: 'No strategies declared',
  coherenceEvaluatedNotTaught: 'This {entity} is assessed, but no {activity} in the design works on it.',
  coherenceTaughtNotEvaluated: 'This {entity} is taught, but no {evaluation} measures it.',
  coherenceSharedStrategies: 'In common: {names}',
  coherenceNoSharedStrategies:
    'What is taught and what is assessed share no strategy. That is not necessarily a problem —you can teach with one and assess with another— but it is worth checking that the level of demand is the same.',
  coherenceNoEvidences: 'No evidences declared',
  coherenceSharedEvidences: 'What is produced is what is assessed: {names}.',
  coherenceMoments: 'Model moments worked on here:',
  coherenceNoSharedEvidences: 'What the student produces while working is not what the {evaluation} asks them to produce. It may be deliberate, but it is worth checking that they have practised the product they are measured with.',
  coverageNoIndicators: 'The {matter} has no {entity} defined in its Programme.',
  // E39 · Design state: the block summarising size and coverage.
  statsTitle: 'Design state',
  statsNumbersTitle: 'The design in numbers',
  statsAnd: 'and',
  statsCoverageTitle: 'Coverage',
  statsOutOf: '{used} of {total}',
  statsPiecesSplit: '{activities} {activity_label} · {evaluations} {evaluation_label}',
  statsHours: 'Teaching hours',
  statsWeeks: 'Weeks used',
  statsWeeksOverflow: 'Exceeds the {matter} total',
  statsWeighing: 'Total weighting',
  statsWeighingOff: 'Does not add up to 100%',
  statsOk: 'Correct',
  statsSequence: 'Pieces that follow the model sequence',
  statsSequenceDetail:
    'An instructional model is a path: a piece should not start at a moment the design has not reached yet.',
  statsSequenceSkips: 'skips {moments}',
  statsMomentsDistribution: 'How many {entity} fulfil each moment',

  // E48 · The AI analysis of the coverage table triangulation.
  triangulationAiButton: 'Analyse with AI',
  triangulationAiTooltip:
    'Checks with AI whether what is taught, what is assessed and what is declared speak about the same learning.',
  triangulationAiTitle: 'Instructional Design triangulation analysis',
  statsPiecesComplete: 'Complete {entity}',
  statsPiecesCompleteDetail:
    'They declare all three: {entity1}, {entity2} and {entity3}.',
  statsUnitsOverlap: '{entity} where what is assessed is taught',
  statsUnitsOverlapDetail: 'The {activity} and the {evaluation} of the {study_unit} share at least one {entity1} or {entity2}. Only {study_units} with both sides declared are counted.',
  statsLacks: 'no',
  statsMissingTitle: 'Missing:',

  // ========== Moments ==========
  momentNone: 'No moment assigned',
  moduleMomentsIncomplete:
    'The model sequence is left unfinished in this {container}: {moments} still needs to be worked on.',
  moduleClosingMomentMissing:
    'This {container} closes the course but does not reach {moment}: the model sequence is left open.',
  momentEmpty: 'No activities in this moment.',
  momentsMissing:
    'Select an instructional model with defined moments to build the design.',

  // ========== Messages ==========
  loadError: 'The instructional design could not be loaded. Please try again later.',
  saveError: 'Changes could not be saved. Please try again.',
  saved: 'Changes saved.',

  // ========== Deleting an activity-evaluation (E16) ==========
  activityDeleteEvaluationWarning: 'The {evaluation} of this {activity} will also be deleted, along with its instruments and questions. {evaluations} already replicated to sections are left untouched.',
};
